import { IndemnityAgreementSignatureMethod, SuretyAccountId, SuretyType } from "@inrev/common";
import {
	AdminAccountIndemnitySummary,
	useAdminCreateAccountIndemnityAgreement,
	useAdminDownloadIndemnityAgreement,
} from "@portal/api/account/admin";
import { createAccountIndemnityAgreementDtoSchema } from "@portal/api/account/agent/validation";
import { FormField } from "@portal/components/form/FormField";
import { FormFieldGroup } from "@portal/components/form/FormFieldGroup";
import { FormItemLabel } from "@portal/components/form/FormItemLabel";
import { FormDropdown } from "@portal/components/form/fields/FormDropdown";
import { FormInput } from "@portal/components/form/fields/FormInput";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { Button, Icon, Text } from "@portal/components/primitives";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { typedZodResolver } from "../../../../../utils/form";
import { IndemnityFormData } from "../../../types";

type AdminAccountCreateIndemnityAgreementProps = {
	accountId: SuretyAccountId;
	indemnitySummary: AdminAccountIndemnitySummary;
	/** A required company has no address, so no signature method can be chosen yet. */
	blockedOnAddress?: boolean;
};

export const AdminAccountCreateIndemnityAgreement = ({
	accountId,
	indemnitySummary,
	blockedOnAddress = false,
}: AdminAccountCreateIndemnityAgreementProps) => {
	const formMethods = useForm<IndemnityFormData>({
		defaultValues: {
			suretyType: SuretyType.contract,
			signatureMethod: null,
			companySigners: indemnitySummary.requiredIndemnitors.companies.map((c) => ({
				contactId: c.id,
				signerEmail: null,
			})),
			individualSigners: indemnitySummary.requiredIndemnitors.individuals.map((i) => ({
				contactId: i.id,
				signerEmail: i.email ?? null,
			})),
		},
		reValidateMode: "onBlur",
		resolver: typedZodResolver(createAccountIndemnityAgreementDtoSchema),
	});

	const watchedSignatureMethod = formMethods.watch("signatureMethod");

	const signatureMethodOptions = useMemo(() => {
		return IndemnityAgreementSignatureMethod.options().filter((opt) =>
			indemnitySummary.allowedSignatureMethods.includes(opt.value),
		);
	}, [indemnitySummary.allowedSignatureMethods]);

	const { createAccountIndemnityAgreement, createAccountIndemnityAgreementIsLoading } =
		useAdminCreateAccountIndemnityAgreement();
	const { downloadIndemnityAgreement } = useAdminDownloadIndemnityAgreement(accountId);

	const handleSubmit = async (data: IndemnityFormData) => {
		try {
			const dto = createAccountIndemnityAgreementDtoSchema.parse(data);
			await createAccountIndemnityAgreement({ accountId, data: dto });
			if (dto.signatureMethod === IndemnityAgreementSignatureMethod.physical) {
				downloadIndemnityAgreement();
			}
		} catch {
			return;
		}
	};

	return (
		<div
			data-testid="admin-account-create-indemnity-agreement"
			className="flex flex-col space-y-[20px] mt-[12px]"
		>
			<div className="flex flex-col space-y-[8px]">
				<Text type="caption" color="secondary">
					Signature Method
				</Text>
				<FormDropdown
					compact
					control={formMethods.control}
					name="signatureMethod"
					options={signatureMethodOptions}
					placeholder="Select one"
					buttonClassName="bg-surface-base"
					disabled={blockedOnAddress}
				/>
			</div>
			{watchedSignatureMethod !== null &&
				match(watchedSignatureMethod)
					.with(IndemnityAgreementSignatureMethod.electronic, () => (
						<div className="flex flex-col space-y-[15px]">
							{indemnitySummary.requiredIndemnitors.companies.map((company, index) => (
								<FormFieldGroup key={company.id} className="py-[8px] rounded-sm space-y-[10px]">
									<div className="flex items-center space-x-[10px]">
										<Text type="caption" color="tertiary">
											{index + 1}.
										</Text>
										<Text type="body-strong" color="primary">
											{company.name}
										</Text>
									</div>
									<FormField condensed className="px-[18px]">
										<FormItemLabel condensed required>
											Signer Email
										</FormItemLabel>
										<FormInput
											control={formMethods.control}
											name={`companySigners.${index}.signerEmail`}
											className="bg-surface-base"
											compact
										/>
									</FormField>
								</FormFieldGroup>
							))}
							{indemnitySummary.requiredIndemnitors.individuals.map((individual, index) => (
								<FormFieldGroup key={individual.id} className="py-[8px] rounded-sm space-y-[10px]">
									<div className="flex items-center space-x-[10px]">
										<Text type="caption" color="tertiary">
											{indemnitySummary.requiredIndemnitors.companies.length + index + 1}.
										</Text>
										<Text type="body-strong" color="primary">
											{individual.name}
										</Text>
									</div>
									<FormField condensed className="px-[18px]">
										<FormItemLabel condensed required>
											Email
										</FormItemLabel>
										<FormInput
											control={formMethods.control}
											name={`individualSigners.${index}.signerEmail`}
											className="bg-surface-base"
											compact
										/>
									</FormField>
								</FormFieldGroup>
							))}
							<ConfirmationModalClickWrapper
								message="Are you sure you want to send this indemnity agreement for e-signatures?"
								onConfirm={() => formMethods.handleSubmit(handleSubmit)()}
								className="max-w-[500px] self-center"
							>
								<Button
									color="primary"
									className="px-[50px] self-center"
									loading={createAccountIndemnityAgreementIsLoading}
									leadingIcon={<Icon type="envelope" size="mini" color="inverse" />}
								>
									Send Indemnity Agreement For E-Signature
								</Button>
							</ConfirmationModalClickWrapper>
						</div>
					))
					.with(IndemnityAgreementSignatureMethod.physical, () => (
						<ConfirmationModalClickWrapper
							message="Are you sure you want to generate this indemnity agreement for wet signatures?"
							onConfirm={() => formMethods.handleSubmit(handleSubmit)()}
							className="max-w-[500px] self-center"
						>
							<Button
								color="primary"
								className="px-[50px] self-center"
								loading={createAccountIndemnityAgreementIsLoading}
								leadingIcon={<Icon type="document-arrow-down" size="mini" color="inverse" />}
							>
								Generate Indemnity Agreement
							</Button>
						</ConfirmationModalClickWrapper>
					))
					.exhaustive()}
		</div>
	);
};
